import {
  HarbourOffice,
  OrderedBy,
  PermitCategory,
} from "../../../../common/enums";
import { ParticipantListItem } from "../../../../components/Forms/Permits/ParticipantsList";
import EnergyHarbourPermitRequest from "../../../requests/EnergyHarbourPermitRequest";
import FormElement, { formElement } from "../../FormElement";
import PersonFormElement, {
  PersonFormElement_default,
  PersonFormElement_toPerson,
  PersonFormElement_populateErrorMessages,
  PersonFormElements_populateErrorMessages,
} from "../../PersonFormElement";
import HotWorkCategoryOneOptionsFormState, {
  HotWorkCategoryOneOptionsFormState_default,
  HotWorkCategoryOneOptionsFormState_fromFormState,
} from "./HotWorkCategoryOneOptionsFormState";
import HotWorkCategoryTwoOptionsFormState, {
  HotWorkCategoryTwoOptionsFormState_default,
  HotWorkCategoryTwoOptionsFormState_fromFormState,
} from "./HotWorkCategoryTwoOptionsFormState";
import HotWorkConditionsFormState, {
  HotWorkFormState_defaultHotWorkCatOneConditions,
  HotWorkFormState_defaultHotWorkCatThreeConditions,
  HotWorkFormState_defaultHotWorkCatTwoConditions,
  HotWorkFormState_toRequest,
} from "./HotWorkFormState";
import PermitAgreementFormState from "./PermitAgreementFormState";
import MeasuresFormState, {
  MeasuresFormState_default,
  MeasuresFormState_fromFormState,
} from "../Conditions/MeasuresFormState";
import ProtectiveEquipmentFormState, {
  ProtectiveEquipmentFormState_default,
  ProtectiveEquipmentFormState_fromFormState,
} from "../Conditions/ProtectiveEquipmentFormState";
import RiskIdentificationsFormState, {
  RiskIdentificationsFormState_default,
  RiskIdentificationsFormState_fromFormState,
  RiskIdentificationsFormState_populateErrorMessages,
} from "../Conditions/RiskIdentificationsFormState";
import PositionPickerRow, {
  PositionPickerRow_default,
  PositionPickerRow_toPosition,
  PositionPickerRows_populateErrorMessages,
} from "../PositionPickerRow";
import Errors from "../../../responses/Errors";
import { FormState_PopulateErrorMessages } from "../../../../common/formStateUtils";
import { String_removePrefix } from "../../../../common/stringUtils";
import {
  Date_toDateString,
  Date_toDateTimeString,
} from "../../../../common/dateUtils";
import HotWorkCategoryThreeOptionsFormState, {
  HotWorkCategoryThreeOptionsFormState_default,
  HotWorkCategoryThreeOptionsFormState_fromFormState,
} from "./HotWorkCategoryThreeOptionsFormState";
import { EnergyHarbourCustomer } from "../../../EnergyHarbourCustomer";

type EnergyHarbourPermitFormState = {
  permitCategories: FormElement<PermitCategory[]>;
  entrepreneur: FormElement<string>;
  description: FormElement<string>;
  startDate: FormElement<Date>;
  endDate: FormElement<Date>;
  persons: ParticipantListItem[];
  positions: PositionPickerRow[];
  remindMe: FormElement<boolean>;
  expirationReminderEmail: FormElement<string | undefined>;
  expirationReminderPhoneNumber: FormElement<string | undefined>;
  remindMeDate: FormElement<Date>;
  harbourOffice: FormElement<HarbourOffice>;
  educationCheckPerformed: FormElement<boolean>;
  measures: MeasuresFormState;
  protectiveEquipment: ProtectiveEquipmentFormState;
  riskIdentifications: RiskIdentificationsFormState;
  hotWork: HotWorkConditionsFormState;
  hotWorkCategoryOneConditions: HotWorkConditionsFormState;
  hotWorkCategoryTwoConditions: HotWorkConditionsFormState;
  hotWorkCategoryThreeConditions: HotWorkConditionsFormState;
  permitAgreement: PermitAgreementFormState;
  hotWorkCategoryOneOptions: HotWorkCategoryOneOptionsFormState;
  hotWorkCategoryTwoOptions: HotWorkCategoryTwoOptionsFormState;
  hotWorkCategoryThreeOptions: HotWorkCategoryThreeOptionsFormState;
  workLeader: PersonFormElement;
  projectLeader: PersonFormElement;
  allInvolvedHaveSafeHarbourEducation: FormElement<boolean>;
  canAttestPermitDone: FormElement<boolean>;
  canAttestPermitTidyed: FormElement<boolean>;
  orderedBy: FormElement<OrderedBy | undefined>;
  orderedByCustomer: FormElement<
    | { companyId: string | undefined; companyDepartmentId: number | undefined }
    | undefined
  >;
};

export default EnergyHarbourPermitFormState;

export function EnergyHarbourPermitFormState_default(): EnergyHarbourPermitFormState {
  return {
    permitCategories: formElement([PermitCategory.ColdWork]),
    allInvolvedHaveSafeHarbourEducation: formElement(true),
    canAttestPermitDone: formElement(false),
    canAttestPermitTidyed: formElement(false),
    description: formElement(""),
    educationCheckPerformed: formElement(false),
    endDate: formElement(new Date()),
    entrepreneur: formElement(""),
    expirationReminderEmail: formElement(""),
    expirationReminderPhoneNumber: formElement(""),
    harbourOffice: formElement(HarbourOffice.Skarvik),
    hotWork: {
      distanceFromPumpsAndVentilationMeters: formElement(25),
      fireDetectorsTurnedOffIsChecked: formElement(false),
      fireDetectorsTurnedOffSectionNames: formElement(""),
      fireExtinguisherCount: formElement(2),
      fireExtinguisherIsChecked: formElement(true),
      fireExtinguisherKg: formElement(12),
      fireGuardCount: formElement(""),
      informCloseDepotBeforeWorkIsChecked: formElement(false),
      isCleanedUpForFireProtection: formElement(true),
      isCloseDepotInformedBeforeWorkIsStarted: formElement(false),
      isContinousGasMeasurements: formElement(true),
      isGasDeclared: formElement(false),
      isPhoneClassified: formElement(true),
      isShipInformedAboutFlammablesSub30Celcius: formElement(false),
      isSparkProtected: formElement(false),
      isWaterFromFirePostInPlace: formElement(false),
      isWellsProtected: formElement(false),
      nameOfCloseDepotBeforeWorkIsStartedInfo: formElement(""),
      noHotWorkAtQuay: formElement(false),
      noHotWorkCloseToShipsHandlingFlammables: formElement(false),
      noHotworkWithFlammableMaterialsUsedSub30Celcius: formElement(true),
      otherInformation: formElement(""),
      postWorkPlaceMonitoredHours: formElement(""),
      workPlaceMonitoredIsChecked: formElement(false),
    },
    hotWorkCategoryOneConditions:
      HotWorkFormState_defaultHotWorkCatOneConditions(),
    hotWorkCategoryTwoConditions:
      HotWorkFormState_defaultHotWorkCatTwoConditions(),
    hotWorkCategoryThreeConditions:
      HotWorkFormState_defaultHotWorkCatThreeConditions(),

    hotWorkCategoryOneOptions: HotWorkCategoryOneOptionsFormState_default(),
    hotWorkCategoryTwoOptions: HotWorkCategoryTwoOptionsFormState_default(),
    hotWorkCategoryThreeOptions: HotWorkCategoryThreeOptionsFormState_default(),
    measures: MeasuresFormState_default(),
    orderedBy: formElement(undefined),
    orderedByCustomer: formElement(undefined),
    permitAgreement: {
      endDate: formElement(new Date()),
      fireGuard: formElement(""),
      isColdWorkOnly: formElement(false),
      startDate: formElement(new Date()),
      wayOfCommunication: formElement(""),
    },
    persons: [{ index: 0, isActive: false, name: "", phoneNumber: "" }],
    positions: [PositionPickerRow_default()],
    projectLeader: PersonFormElement_default(),
    protectiveEquipment: ProtectiveEquipmentFormState_default(),
    remindMe: formElement(false),
    remindMeDate: formElement(new Date()),
    riskIdentifications: RiskIdentificationsFormState_default(),
    startDate: formElement(new Date()),
    workLeader: PersonFormElement_default(),
  };
}

export function EnergyHarbourPermitFormState_toRequest(
  formState: EnergyHarbourPermitFormState,
): EnergyHarbourPermitRequest {
  const request: EnergyHarbourPermitRequest = {
    allInvolvedHaveSafeHarbourEducation:
      formState.allInvolvedHaveSafeHarbourEducation.value,
    description: formState.description.value,
    educationCheckPerformed: formState.educationCheckPerformed.value,
    endDate: Date_toDateString(formState.endDate.value),
    entrepreneur: formState.entrepreneur.value,
    expirationReminderEmail: formState.expirationReminderEmail.value,
    expirationReminderPhoneNumber:
      formState.expirationReminderPhoneNumber.value,
    harbourOffice: formState.harbourOffice.value,
    hotWork: HotWorkFormState_toRequest(formState.hotWork),
    hotWorkCategoryOneConditions: HotWorkFormState_toRequest(
      formState.hotWorkCategoryOneConditions,
    ),
    hotWorkCategoryTwoConditions: HotWorkFormState_toRequest(
      formState.hotWorkCategoryTwoConditions,
    ),
    hotWorkCategoryThreeConditions: HotWorkFormState_toRequest(
      formState.hotWorkCategoryThreeConditions,
    ),
    hotWorkCategoryOneOptions: HotWorkCategoryOneOptionsFormState_fromFormState(
      formState.hotWorkCategoryOneOptions,
    ),
    hotWorkCategoryTwoOptions: HotWorkCategoryTwoOptionsFormState_fromFormState(
      formState.hotWorkCategoryTwoOptions,
    ),
    hotWorkCategoryThreeOptions:
      HotWorkCategoryThreeOptionsFormState_fromFormState(
        formState.hotWorkCategoryThreeOptions,
      ),
    measures: MeasuresFormState_fromFormState(formState.measures),
    orderedBy: formState.orderedBy.value,
    orderedByCompanyDepartmentId:
      formState.orderedByCustomer.value?.companyDepartmentId,
    orderedByCompanyId: formState.orderedByCustomer.value?.companyId,
    permitAgreement: {
      endDate: Date_toDateTimeString(formState.permitAgreement.endDate.value),
      fireGuard: formState.permitAgreement.fireGuard.value,
      isColdWorkOnly: formState.permitAgreement.isColdWorkOnly.value,
      startDate: Date_toDateTimeString(
        formState.permitAgreement.startDate.value,
      ),
      wayOfCommunication: formState.permitAgreement.wayOfCommunication.value,
    },
    persons: formState.persons.map((p) => ({
      isActive: p.isActive,
      name: p.name,
      phoneNumber: p.phoneNumber,
    })),
    positions: formState.positions.map(PositionPickerRow_toPosition),
    projectLeader: PersonFormElement_toPerson(formState.projectLeader),
    protectiveEquipment: ProtectiveEquipmentFormState_fromFormState(
      formState.protectiveEquipment,
    ),
    remindMe: formState.remindMe.value,
    remindMeDate: formState.remindMeDate.value,
    riskIdentifications: RiskIdentificationsFormState_fromFormState(
      formState.riskIdentifications,
    ),
    startDate: Date_toDateString(formState.startDate.value),
    workLeader: PersonFormElement_toPerson(formState.workLeader),
    permitCategories: formState.permitCategories.value,
    clearSignaturesConfirmed: false,
  };

  return request;
}

export function EnergyHarbourPermitFormState_populateErrorMessages(
  formState: EnergyHarbourPermitFormState,
  errors: Errors,
): EnergyHarbourPermitFormState {
  const errorKeys = Object.keys(errors);

  const riskIdentificationErrorKeys = errorKeys.filter((k) =>
    k.startsWith("RiskIdentifications"),
  );
  const riskIdentificationErrors: Errors = Object.fromEntries(
    riskIdentificationErrorKeys.map((k) => [
      String_removePrefix(k, "RiskIdentifications."),
      errors[k],
    ]),
  );

  const measuresErrorKeys = errorKeys.filter((k) => k.startsWith("Measures"));
  const measuresErrors = Object.fromEntries(
    measuresErrorKeys.map((k) => [
      String_removePrefix(k, "Measures."),
      errors[k],
    ]),
  );

  const protectiveEquipmentErrorKeys = errorKeys.filter((k) =>
    k.startsWith("ProtectiveEquipment"),
  );
  const protectiveEquipmentErrors = Object.fromEntries(
    protectiveEquipmentErrorKeys.map((k) => [
      String_removePrefix(k, "ProtectiveEquipment."),
      errors[k],
    ]),
  );

  const hotWorkErrorKeys = errorKeys.filter(
    (k) => k.startsWith("HotWork") && !k.includes("hotWorkCategory"),
  );
  const hotWorkErrors = Object.fromEntries(
    hotWorkErrorKeys.map((k) => [
      String_removePrefix(k, "HotWork."),
      errors[k],
    ]),
  );

  const permitAgreementErrorKeys = errorKeys.filter((k) =>
    k.startsWith("PermitAgreement"),
  );
  const permitAgreementErrors = Object.fromEntries(
    permitAgreementErrorKeys.map((k) => [
      String_removePrefix(k, "PermitAgreement."),
      errors[k],
    ]),
  );

  const positionsErrorKeys = errorKeys.filter((e) => e.startsWith("Positions"));
  const positionErrors = Object.fromEntries(
    positionsErrorKeys.map((k) => [
      String_removePrefix(k, "Positions"),
      errors[k],
    ]),
  );

  const personsErrorKeys = errorKeys.filter((e) => e.startsWith("Persons"));
  const personErrors = Object.fromEntries(
    personsErrorKeys.map((k) => [String_removePrefix(k, "Persons"), errors[k]]),
  );

  const workLeaderErrorKeys = errorKeys.filter((e) =>
    e.startsWith("WorkLeader"),
  );
  const workLeaderErrors = Object.fromEntries(
    workLeaderErrorKeys.map((k) => [
      String_removePrefix(k, "WorkLeader."),
      errors[k],
    ]),
  );

  const projectLeaderErrorKeys = errorKeys.filter((e) =>
    e.startsWith("ProjectLeader"),
  );
  const projectLeaderErrors = Object.fromEntries(
    projectLeaderErrorKeys.map((k) => [
      String_removePrefix(k, "ProjectLeader."),
      errors[k],
    ]),
  );

  const updatedFormState = FormState_PopulateErrorMessages(formState, errors);

  updatedFormState.riskIdentifications =
    RiskIdentificationsFormState_populateErrorMessages(
      formState.riskIdentifications,
      riskIdentificationErrors,
    );

  updatedFormState.measures = FormState_PopulateErrorMessages(
    formState.measures,
    measuresErrors,
  );
  updatedFormState.protectiveEquipment = FormState_PopulateErrorMessages(
    formState.protectiveEquipment,
    protectiveEquipmentErrors,
  );
  updatedFormState.hotWork = FormState_PopulateErrorMessages(
    formState.hotWork,
    hotWorkErrors,
  );
  updatedFormState.permitAgreement = FormState_PopulateErrorMessages(
    formState.permitAgreement,
    permitAgreementErrors,
  );

  updatedFormState.positions = PositionPickerRows_populateErrorMessages(
    formState.positions,
    positionErrors,
  );
  updatedFormState.persons = PersonFormElements_populateErrorMessages(
    formState.persons,
    personErrors,
  );
  updatedFormState.workLeader = PersonFormElement_populateErrorMessages(
    formState.workLeader,
    workLeaderErrors,
  );
  updatedFormState.projectLeader = PersonFormElement_populateErrorMessages(
    formState.projectLeader,
    projectLeaderErrors,
  );

  return updatedFormState;
}
